import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import * as constants from './constants.js';

import TableHeaderCell from './TableHeaderCell.jsx';
import ConfirmationDialog from '../ConfirmationDialog.jsx';

import { DateTime } from 'luxon';

function DataTable(props) {
  const [checkboxesState, setCheckboxesState] = useState(props.checkboxesState || {});
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(props.isSelectAllChecked || false);
  const [tooltip, setTooltip] = useState({ visible: false, content: '', top: 0, left: 0 });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [deleteContext, setDeleteContext] = useState({});
  
  const getColumnCount = (displayColumns, columnVisibility, rowActions, showCheckboxes) => {
    let columnCount = displayColumns.length;
    columnCount += (columnVisibility[constants.SPECIAL_COLUMN_NOTIFICATION] ? 1 : 0);
    columnCount += (showCheckboxes ? 1 : 0);
    displayColumns.forEach(column => {
      if (!columnVisibility[column.value]) {
        columnCount--;
      }
    });
    if (!!rowActions && rowActions.length > 0) {
      columnCount++;
    }
    return columnCount;
  };

  const handleCheckboxChange = useCallback((rowId) => {
    setCheckboxesState(prevState => ({ ...prevState, [rowId]: !prevState[rowId] }));
    if (props.onCheckboxStateChange) {
        props.onCheckboxStateChange(rowId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onCheckboxStateChange]);

  const handleSelectAllCheckboxChange = useCallback(() => {
    setIsSelectAllChecked(prevState => !prevState);
    const newCheckboxesState = props.data.reduce((acc, row) => {
      if (!props.checkboxDisplayCriteriaFunction || props.checkboxDisplayCriteriaFunction(row)) {
        acc[row.id] = !isSelectAllChecked;
      }
      return acc;
    }, {});
    setCheckboxesState(newCheckboxesState);
    if (props.onSelectAllCheckboxStateChange) {
      props.onSelectAllCheckboxStateChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data, props.checkboxDisplayCriteriaFunction, props.onSelectAllCheckboxStateChange, isSelectAllChecked]);

  const handleCellMouseEnter = (e, content) => {
    if (props.textOverflowBehavior !== 'ellipsis' || !content) return;

    const cell = e.currentTarget;
    if (cell.scrollWidth > cell.clientWidth) {
      const rect = cell.getBoundingClientRect();
      setTooltip({
        visible: true,
        content: content,
        top: rect.bottom + 5,
        left: rect.left
      });
    }
  };

  const handleCellMouseLeave = () => {
    if (tooltip.visible) {
      setTooltip(prev => ({ ...prev, visible: false }));
    }
  };

  const renderTable = (data, displayColumns, columnVisibility, rowActions, showCheckboxes) => {
    const columnCount = getColumnCount(displayColumns, columnVisibility, rowActions, showCheckboxes);

    return (
      <table className='truncate-cells'>
        <thead>
          <tr key="hrow">
            <TableHeaderCell 
              display={columnVisibility[constants.SPECIAL_COLUMN_NOTIFICATION]} 
              className="col-fixed-small"
              key={constants.SPECIAL_COLUMN_NOTIFICATION} 
            />

            {showCheckboxes && (
              <TableHeaderCell 
                display={columnVisibility[constants.SPECIAL_COLUMN_CHECKBOX]} 
                className="col-fixed-small"
                key={constants.SPECIAL_COLUMN_CHECKBOX}
              >
                <input
                  checked={isSelectAllChecked}
                  name='note_SelectAll'
                  onChange={handleSelectAllCheckboxChange}
                  type="checkbox"
                  value='__selectAll'
                />
              </TableHeaderCell>
            )}

            {displayColumns.map((displayColumn) => renderHeaderCell(displayColumn, columnVisibility[displayColumn.value], props.onSort, props.currentSortOrderAttribute, props.isCurrentSortOrderAscending))}
            <TableHeaderCell 
              display={!!rowActions?.length} 
              className="col-actions-header"
              key={constants.SPECIAL_COLUMN_ROWACTIONS} 
            />
          </tr>
        </thead>
        <tbody>
          {data && data.length > 0 ?
            data.map((row) => renderData(row, displayColumns, columnVisibility, rowActions, showCheckboxes))
            : props.isProcessing ? null :
              <tr><td colSpan={columnCount}>{props.emptyTableMessage}</td></tr>
          }
        </tbody>
      </table>
    );
  };

  const renderHeaderCell = (displayColumn, columnVisibility, onSort, currentSortOrderAttribute, isCurrentSortOrderAscending) => {
    let label = displayColumn.label || null;
    let style = displayColumn.headerStyle || {};
    if(displayColumn.dataType === constants.DATATYPE_INTEGER) {
      style.textAlign = 'right';
    }
    let isSortable = displayColumn.isSortable || false;
    currentSortOrderAttribute = currentSortOrderAttribute || null;

    return (
      <TableHeaderCell label={label} display={columnVisibility}
        style={style} isSortable={isSortable} sortAttribute={displayColumn.value}
        onSort={onSort} currentSortOrderAttribute={currentSortOrderAttribute}
        isCurrentSortOrderAscending={isCurrentSortOrderAscending}
        key={displayColumn.value}
      />
    );
  };

  const renderData = (row, displayColumns, columnVisibility, rowActions, showCheckboxes) => {
    const isChecked = checkboxesState[row.id] || false;
    return (
      <tr 
        key={row.id}
        onClick={() => {
          if (props.enableRowClick && props.onRowClick) {
            props.onRowClick(row);
          }
        }}
        style={{ cursor: props.enableRowClick && props.onRowClick ? 'pointer' : 'default' }}
      >
        {columnVisibility[constants.SPECIAL_COLUMN_NOTIFICATION] === true && (
          <td className="col-fixed-small" key={`${constants.SPECIAL_COLUMN_NOTIFICATION}_${row.id}`}>
            {!!row.errorMessage ?
              <i className='fas fa-exclamation-triangle' style={{ color: 'red', fontSize: '1em', ariaHidden: true }} title={row.errorMessage}></i>
              : null
            }
          </td>
        )}

        {showCheckboxes && (
          <td className="col-fixed-small" key={`${constants.SPECIAL_COLUMN_CHECKBOX}_${row.id}`}>
            {(!props.checkboxDisplayCriteriaFunction || props.checkboxDisplayCriteriaFunction(row)) && (
              <input
                checked={isChecked}
                name={`${props.checkboxNamePrefix}_SelectSingle`}
                onChange={() => handleCheckboxChange(row.id)}
                type="checkbox"
                value={row.id}
                id={row.id}
              />
            )}
          </td>
        )}

        {displayColumns.map((displayColumn, i) => renderDataCell(displayColumn, i, row, columnVisibility))}
        {!!rowActions && rowActions.length > 0 && (
          <td className='actions' key={`${constants.SPECIAL_COLUMN_ROWACTIONS}_${row.id}`}>
            <div className='actions-wrapper'>
              {rowActions.map((rowAction, index) => renderRowAction(rowAction, index, row))}
            </div>
          </td>
        )}
      </tr>
    );
  };

  const renderRowAction = (rowAction, index, row) => {
    if(rowAction.isDelete && !props.hasDeletePermission) return null;

    return (
      (!rowAction.displayCriteriaFunction || rowAction.displayCriteriaFunction(row) === true) ?
        <div onClick={(event) => {onRowActionClick(event, row, rowAction)}} title={rowAction.tooltipText}
          className={rowAction.customRenderer ? '' : (rowAction.class ?? '')}
          key={`${constants.SPECIAL_COLUMN_ROWACTIONS}_${row.id}_${index}`}
        >
          {rowAction.customRenderer ? rowAction.customRenderer(row) : rowAction.label}
        </div>
        :
        null
    );
  };

  const getColumnValue = (displayColumn, row) => {
    if(!!displayColumn.valueFunction) {
      return displayColumn.valueFunction(row[displayColumn.value], row, displayColumn.params)
    }
    return formatCellData(displayColumn, row[displayColumn.value])
  }

  const renderDataCell = (displayColumn, i, row, columnVisibility) => {
    let isColumnVisible = columnVisibility[displayColumn.value] || false;
    let style = displayColumn.style || {};
    if(displayColumn.dataType === constants.DATATYPE_INTEGER) {
      style.textAlign = 'right';
    }
    if (isColumnVisible) {
      const isLinkedToEditScreen = props.hasEditPermission && displayColumn.isLinkedToEditScreen === true;
      const cellValue = getColumnValue(displayColumn, row);
      const renderedValue = React.isValidElement(cellValue) ? cellValue : (cellValue ?? '') + '';
      
      const cellTextClass = props.textOverflowBehavior === 'ellipsis' ? 'cell-text-ellipsis' : 'cell-text-wrap';
      
      return (
        <td 
          className={`${isLinkedToEditScreen ? 'edit-link' : ''} ${cellTextClass}`}
          style={style} 
          onClick={() => {if(isLinkedToEditScreen) {props.onEditLinkClick(row)} }}
          key={`${displayColumn.value}_${row.id}`}
          onMouseEnter={(e) => handleCellMouseEnter(e, renderedValue)}
          onMouseLeave={handleCellMouseLeave}
        >
          {renderedValue}
        </td>
      );
    }
    return null;
  };

  const formatCellData = (displayColumn, obj) => {
    if (obj === null || obj === undefined || obj === '') {
      return '';
    }
    if (displayColumn.dataType === constants.DATATYPE_BOOLEAN_YES_NO) {
      return obj === true ? 'Yes' : 'No';
    }
    if (!displayColumn.dataType || !displayColumn.format) {
      return obj;
    }
    if (displayColumn.dataType === constants.DATATYPE_DATE) {
      return DateTime.fromISO(obj.substring(0, 10)).toFormat(displayColumn.format || 'LL/dd/yyyy');
    }
    if (displayColumn.dataType === constants.DATATYPE_TIME) {
      return DateTime.fromISO(obj).toFormat(displayColumn.format || 'hh:mm:ss a');
    }
    if (displayColumn.dataType === constants.DATATYPE_DATETIME) {
      return DateTime.fromISO(obj).toFormat(displayColumn.format || 'LL/dd/yyyy hh:mm:ss a');
    }
    return obj;
  };

  const onRowActionClick = (event, row, rowAction) => {
    if (!rowAction.isDelete) {
      const result = rowAction.handler(event, row);
      if (result && typeof result.then === 'function') {
        result.then((updatedRow) => {
          if (updatedRow && props.onUpdateRow) {
            props.onUpdateRow(updatedRow);
          }
        }).catch(() => {
            // Error is already handled in the handler, so no need to log here.
        });
      }
      return;
    }
    
    if(!props.hasDeletePermission) return;

    if(!!rowAction?.requireConfirmation) {
      setDeleteContext({row, rowAction});
      setShowConfirmation(true);
    }
    else {
      onDeleteRowAction(event, row, rowAction)
    }
  };

  const onDeleteRowAction = (event, row, rowAction) => {
    rowAction.handler(event, row)
    .then(() => {
      props.onDelete(row)
    })
    .catch(err => {
      console.error('Error calling rowAction', rowAction?.tooltipText, '- Message:', err.message);
    });
  }

  const handleConfirmDelete = async (event) => {
    const {row, rowAction} = deleteContext;
    
    try {
      await rowAction.handler(event, row);
      props.onDelete(row);
      setShowConfirmation(false);
      setDeleteContext({});
    } catch (err) {
      console.error('Error calling rowAction', rowAction?.tooltipText, '- Message:', err.message);
      setShowConfirmation(false);
      setDeleteContext({});
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmation(false);
    setDeleteContext({});
  };
  
  return (
    <>
      <TransitionGroup>
        <CSSTransition classNames="fade" timeout={700}>
          <div className='data-table'>
            {renderTable(props.data, props.displayColumns, props.columnVisibility, props.rowActions, props.showCheckboxes)}
          </div>
        </CSSTransition>
      </TransitionGroup>
      
      <div 
        className={`custom-tooltip ${tooltip.visible ? 'visible' : ''}`}
        style={{ top: tooltip.top, left: tooltip.left }}
      >
        {tooltip.content}
      </div>

      <ConfirmationDialog
        isOpen={showConfirmation}
        title={deleteContext.rowAction?.confirmationTitle || "Confirm Deletion"}
        message={deleteContext.rowAction?.confirmationMessage || "Are you sure you want to delete this item?"}
        icon={deleteContext.rowAction?.confirmationIcon || "warning"}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmLabel={deleteContext.rowAction?.confirmLabel || "Yes, Delete"}
        cancelLabel={deleteContext.rowAction?.cancelLabel || "No, Cancel"}
      />
    </>
);
}

DataTable.propTypes = {
  displayColumns: PropTypes.array.isRequired
  , columnVisibility: PropTypes.object.isRequired
  , data: PropTypes.array.isRequired
  , onSort: PropTypes.func
  , currentSortOrderAttribute: PropTypes.string
  , isCurrentSortOrderAscending: PropTypes.bool
  , checkboxesState: PropTypes.object
  , checkboxNamePrefix: PropTypes.string
  , onSelectAllCheckboxStateChange: PropTypes.func
  , onCheckboxStateChange: PropTypes.func
  , isSelectAllChecked: PropTypes.bool
  , checkboxDisplayCriteriaFunction: PropTypes.func
  , rowActions: PropTypes.array
  , animate: PropTypes.bool
  , emptyTableMessage: PropTypes.string
  , isProcessing: PropTypes.bool
  , link: PropTypes.bool
  , getLinkURLFunction: PropTypes.func
  , getLinkURLFunctionParams: PropTypes.object
  , onUpdateRow: PropTypes.func
  , showCheckboxes: PropTypes.bool
  , textOverflowBehavior: PropTypes.oneOf(['wrap', 'ellipsis'])
  , enableRowClick: PropTypes.bool
  , onRowClick: PropTypes.func
};

DataTable.defaultProps = {
  onSort: null
  , currentSortOrderAttribute: ''
  , isCurrentSortOrderAscending: true
  , checkboxesState: {}
  , checkboxNamePrefix: ''
  , onSelectAllCheckboxStateChange: null
  , onCheckboxStateChange: null
  , isSelectAllChecked: false
  , checkboxDisplayCriteriaFunction: null
  , rowActions: null
  , animate: false
  , emptyTableMessage: 'No records to display'
  , isProcessing: false
  , link: false
  , getLinkURLFunction: null
  , getLinkURLFunctionParams: null
  , onUpdateRow: null
  , showCheckboxes: false
  , textOverflowBehavior: 'ellipsis'
  , enableRowClick: false
  , onRowClick: null
};

export default DataTable;